import "plugins/gesture-handler";
import "./plugins/IntlPolyfill";
import { Platform } from "react-native";
import React, { useEffect } from "react";
import { SafeAreaProvider } from "react-native-safe-area-context";
import useCachedResources from "./hooks/useCachedResources";
import Navigation from "./navigation";
import * as Linking from "expo-linking";
// redux
import { Provider } from "react-redux";
import { store } from "./store";
import { useAppSelector } from "hooks/useAppSelector";
import { useAppDispatch } from "hooks/useAppDispatch";
import { initTenantSubdomainAndType } from "features/slices/tenantSlice";

// ui-library
import { ApplicationProvider } from "@ui-kitten/components";
import * as eva from "@eva-design/eva";
import Toast from "react-native-toast-message";
import { ActionSheetProvider } from "@expo/react-native-action-sheet";

// plugins
import "./plugins/localization";
import "./plugins/initDayjs";
import "./plugins/santry";

// others
import toastConfig from "config/toastConfig";
import { default as mapping } from "config/evaMapping.json";
import "./styles/tailwind.css";
import "./styles/index.css";
import ReactGA from "react-ga4";
import { isDev } from "config/env";
import { GestureHandlerRootView } from "react-native-gesture-handler";

// react query
import { QueryClientProvider } from "@tanstack/react-query";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import { tryLocalSignIn } from "features/slices/authSlice";
import { queryClient } from "plugins/queryClient";

import "@expo/metro-runtime";
import {
  loadFacebookPixel,
  loadGoogleAnalytics,
  loadTikTokPixel,
  loadTwitterPixel,
} from "utils/analytics";
import {
  getFacebookPixelIdBySubdomain,
  getTiktokPixelIdBySubdomain,
  getTrackingIdsBySubdomain,
  getTwitterPixelIdBySubdomain,
} from "clients/TenantTrackingIds";

if (process.env.NODE_ENV === "production") {
  console.log = () => {};
  console.error = () => {};
  console.debug = () => {};
}

export default function AppWrapper() {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <ActionSheetProvider>
          <GestureHandlerRootView style={{ flex: 1 }}>
            <App />
          </GestureHandlerRootView>
        </ActionSheetProvider>
      </Provider>
    </QueryClientProvider>
  );
}

function App() {
  const isLoadingComplete = useCachedResources();
  const { theme, customTheme } = useAppSelector((state) => state.theme);
  const dispatch = useAppDispatch();
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const { data: tenantBranding } = useFetchTenantBrandingQuery(
    Platform.OS === "web"
  );

  const customMapping = Platform.OS === "web" ? mapping : {};

  const initiAnalytics = (tenantSubdomain: string) => {
    if (isDev) return;
    [
      /*FanRally's GA*/ "G-CDD0GBSB3H",
      ...getTrackingIdsBySubdomain(tenantSubdomain),
    ].forEach((trackingId) => {
      loadGoogleAnalytics(trackingId);
    });
    loadFacebookPixel(getFacebookPixelIdBySubdomain(tenantSubdomain));
    loadTwitterPixel(getTwitterPixelIdBySubdomain(tenantSubdomain));
    loadTikTokPixel(getTiktokPixelIdBySubdomain(tenantSubdomain));
  };

  useEffect(() => {
    // workaround for https://github.com/react-navigation/react-navigation/issues/10398
    //                https://github.com/react-navigation/react-navigation/issues/10481
    // waiting for fix for this
    // the bug is that, when user press the first item on drawer/bottom-tab, which is 'Home' in our app,
    // the browser will execute history.go(-1),[note: history.go(-2) if user lands on page other than the home page] triggered by underlying logic of react-navigation
    if (Platform.OS === "web") {
      history.pushState(null, "", "");
      history.pushState(null, "", "");
    }
  }, []);

  useEffect(() => {
    if (!tenantBranding) return;
    dispatch(initTenantSubdomainAndType(tenantBranding));
    initiAnalytics(tenantBranding.subdomain);
  }, [tenantBranding]);

  useEffect(() => {
    dispatch(tryLocalSignIn());
  }, []);

  if (!isLoadingComplete || !tenantConfig) {
    return null;
  }

  if (Platform.OS === "web" && !tenantBranding) {
    return null;
  }

  return (
    <ApplicationProvider
      {...eva}
      theme={theme === "light" ? customTheme : eva.dark}
      customMapping={{ ...eva.mapping, ...customMapping }}
    >
      <SafeAreaProvider>
        <Navigation />
        <Toast config={toastConfig} />
      </SafeAreaProvider>
    </ApplicationProvider>
  );
}
